import * as React from 'react';
import { Fragment } from 'react';
import { compose, setDisplayName, withHandlers, withState, lifecycle, withProps } from 'recompose';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { getLprPlateLists } from 'api';
import { LprPlateList } from 'types';
import { translate } from 'react-i18next';
import { Loading } from 'ui/shared/Loading';
import { Fade, FadeCollapse } from 'ui/transitions';
import { ErrorMessage } from 'ui/shared/ErrorMessage';
import { Button } from 'ui/shared/Button';
const { Transition, animated } = require('react-spring');

import './LprFilterSection.css';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const staticStyles = {
    exit: {
        opacity: 0,
        height: 0,
    },
    enter: {
        opacity: 1,
        height: 30,
    },
    loading: {
        marginTop: '1em',
        fontSize: '.8em',
        opacity: .5,
    },
    pointer: {
        cursor: 'pointer',
    },
    listId: {
        marginLeft: '.5em',
        opacity: .9,
    },
    separator: {
        opacity: .4,
        marginRight: '.5em',
    },
    tryAgain: {
        margin: '1em 0 0 0',
    },
    noData: {
        opacity: .5,
    },
};

const RawLprFilterSection = ({
    keys,
    error,
    isLoading,
    plateLists,
    selectedListIds,
    handleCheckChanged,
    handleClickTryAgain,
    t,
}: Props) =>
    <Fragment>
        <div>
            { t('Reports_FilterPlateLists') }
        </div>
        <FadeCollapse
            visible={ isLoading }
            duration={ 400 }
            style={ staticStyles.loading }
            height={ '2em' }
        >
            <Loading
                size="tiny"
                textKey="6.24407.4553"
                centered={ false }
            />
        </FadeCollapse>
        <FadeCollapse
            visible={ !!error }
            duration={ 400 }
            height={ '3.5em' }
        >
            <ErrorMessage size="1em" text={ error }/>
            <Button
                id="load-plate-list-again"
                textKey="SERVER_CONFIG_BTN_TRY_AGAIN"
                onClick={ handleClickTryAgain }
                style={ staticStyles.tryAgain }
            />
        </FadeCollapse>
        <Fade
            visible={ !isLoading && !error }
            duration={ 200 }
        >
            <ol id="edit-report-selected-plate-lists">
                { plateLists.length === 0 &&
                    <li
                        style={ staticStyles.noData }
                    >
                        { t('WC_REPORT_LPR_NO_PLATE_LISTS') }
                    </li>
                }
                <Transition
                    native={ true }
                    keys={ keys }
                    from={ staticStyles.exit }
                    enter={ staticStyles.enter }
                    leave={ staticStyles.exit }
                >
                    { plateLists.map(({ id, name }) => (style: React.CSSProperties) =>
                        <animated.li style={ style }>
                            <input
                                id={ `report-plate-list-${id}` }
                                type="checkbox"
                                data-listid={ id }
                                checked={ selectedListIds.indexOf(id) >= 0 }
                                onChange={ handleCheckChanged }
                                style={ staticStyles.pointer }
                            />
                            <label
                                htmlFor={ `report-plate-list-${id}` }
                                style={ staticStyles.pointer }
                            >
                                <span
                                    style={ staticStyles.listId }
                                >
                                    { id }
                                </span>
                                <span style={ staticStyles.separator }>)</span>
                                <span>
                                    { name }
                                </span>
                            </label>
                        </animated.li>
                    ) }
                </Transition>
            </ol>
        </Fade>
    </Fragment>
;

type RecomposeProps = {
    keys: string[];
    error: string | null;
    isLoading: boolean;
    plateLists: LprPlateList[];
    selectedListIds: number[];
    handleCheckChanged: React.ChangeEventHandler<HTMLInputElement>;
    handleClickTryAgain: React.MouseEventHandler<HTMLButtonElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setIsLoading: (b: boolean) => void;
    _setPlateLists: (lists: LprPlateList[]) => void;
    _setError: (msg: string | null) => void;
};

const loadPlateLists = async (props: _RecomposeProps & TranslateProps) => {
    const { _setIsLoading, _setPlateLists, _setError, t } = props;
    _setIsLoading(true);
    try {
        // Start a request to fetch the LPR plate lists
        const lists = await getLprPlateLists();
        _setPlateLists(lists.sort((a, b) => a.id - b.id));
    }
    catch (err) {
        _setError(t(err.message));
    }
    finally {
        _setIsLoading(false);
    }
};

type Props = OwnProps & RecomposeProps & TranslateProps;

export const LprFilterSection = compose<Props, OwnProps>(
    setDisplayName('LprFilterSection'),
    translate(),
    withState('isLoading', '_setIsLoading', true),
    withState('error', '_setError', null),
    withState('plateLists', '_setPlateLists', []),
    withProps(({ plateLists, reportSettings }: Props) => ({
        keys: plateLists.map(({ id }) => `${id}`),
        selectedListIds: reportSettings.filter || [],
    })),
    withHandlers<Props & _RecomposeProps, Partial<RecomposeProps>>({
        handleCheckChanged: ({ onChange, selectedListIds }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const listId = parseInt(e.currentTarget.dataset.listid!, 10);
            let newFilter = (selectedListIds || []);
            newFilter =
                e.currentTarget.checked
                    ? [ ...newFilter, listId ]
                    : newFilter.filter(id => id !== listId);

            onChange(reportSettings => reportSettings.setFilter(newFilter));
        },
        handleClickTryAgain: props => () => {
            props._setIsLoading(true);
            props._setError(null);

            // Introduce an artificial delay so animations can run and user can see that something changed
            setTimeout(() => loadPlateLists(props), 400);
        },
    }),
    lifecycle<Props & _RecomposeProps, OwnProps>({
        componentDidMount() {
            loadPlateLists(this.props);
        },
    }),
)(RawLprFilterSection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/filter/lpr/LprFilterSection.tsx